/**
 * The part that has to touch the browser.
 *
 * `save.js` decides what a career *is* and whether a payload may be loaded.
 * This reads and writes it, and is kept thin for the same reason `input.js` is
 * thin next to `inputMap.js`: everything awkward here is about the storage
 * rather than about the game.
 *
 *   - `localStorage` throws rather than returning null in a private window, on
 *     a page with cookies blocked, and when the quota is full. Every call is
 *     wrapped, and a store that cannot be written to degrades to a career that
 *     does not persist rather than to a game that does not boot.
 *   - A half-written value is indistinguishable from a corrupt one, so a parse
 *     failure is treated as *no save* rather than as an error worth stopping
 *     for.
 *   - The storage object is injectable, so the tests run in `node` with a Map
 *     behind them rather than a browser.
 */

import { describeSave, inspectSave, restoreCareer, snapshotCareer } from './save.js';

export const SAVE_KEY = 'fightgame.career.v1';

/** A storage-shaped thing backed by an ordinary Map. Used by the tests. */
export function memoryStore() {
  const held = new Map();
  return {
    getItem: (key) => (held.has(key) ? held.get(key) : null),
    setItem: (key, value) => held.set(key, String(value)),
    removeItem: (key) => held.delete(key),
  };
}

/** The browser's, when there is one to have. */
function browserStore() {
  try {
    const store = globalThis.localStorage;
    // Touching it is the test: a blocked store throws on access, not on use.
    store?.getItem(SAVE_KEY);
    return store ?? null;
  } catch {
    return null;
  }
}

export function createSaveStore({
  ladder,
  campaign,
  player = null,
  bank = null,
  audio = null,
  key = SAVE_KEY,
  storage = browserStore(),
  onWarn = () => {},
} = {}) {
  /** Whether anything has been written since the last flush. */
  let dirty = false;
  let last = null;

  function readRaw() {
    if (!storage) return null;
    try {
      const raw = storage.getItem(key);
      if (!raw) return null;
      return JSON.parse(raw);
    } catch {
      // Unparseable is indistinguishable from never-written, and treating it as
      // an error would leave somebody staring at a game that will not start.
      return null;
    }
  }

  const store = {
    key,
    get available() {
      return Boolean(storage);
    },
    /** What the last load or save decided, for the overlay to show. */
    get status() {
      return last;
    },

    /** Reads what is there and says whether it could be loaded, without loading it. */
    inspect: () => inspectSave(readRaw()),
    /** And what it is, in one line, for a title screen to put on a button. */
    describe: () => describeSave(readRaw()),

    /**
     * Loads the saved career, or explains why it did not.
     *
     * An outdated save is **discarded** rather than left to be found again by
     * the next boot: the reason is reported once, and the slot is cleared so
     * the next save writes a clean one.
     */
    load() {
      const data = readRaw();
      const result = restoreCareer(data, { ladder, campaign, player, bank, audio });
      last = result;
      if (!result.ok && data) {
        onWarn(result.reason);
        // Only a save that *exists* and cannot be used is cleared. "No career
        // saved yet" is not a failure and there is nothing to remove.
        store.clear();
      }
      return result;
    },

    /** Writes the career as it stands. Returns whether it landed. */
    save() {
      if (!storage) return false;
      try {
        storage.setItem(key, JSON.stringify(snapshotCareer({ ladder, campaign, player, bank, audio })));
        dirty = false;
        return true;
      } catch (error) {
        // A full quota is not a reason to stop the game.
        onWarn(`could not save: ${error?.message ?? error}`);
        return false;
      }
    },

    /** Marks the career as changed. Cheap, and safe to call from a listener. */
    touch() {
      dirty = true;
      return dirty;
    },
    get dirty() {
      return dirty;
    },
    /** Writes only if something has changed since the last write. */
    flush() {
      return dirty ? store.save() : false;
    },

    /** Forgets the saved career. Does not touch the live one. */
    clear() {
      if (!storage) return false;
      try {
        storage.removeItem(key);
        return true;
      } catch {
        return false;
      }
    },
  };

  return store;
}
